import { useCallback, useState } from 'react';

import { shipmentToPackageRows } from '@/components/LabelPreviewModal/shipmentLabels';
import { toast } from '@/design-system/toaster';
import type { PackageRow } from '@/services/api/packages';
import { shipmentsAPI } from '@/services/api/shipments';

import { errorMessage, warehouseApi } from '../api/warehouse.api';
import {
  hasThirdPartyShippingLabel,
  outboundLabelToPackageRows,
} from '../print/shippingLabels';
import type { OutboundOrder } from '../types';

/**
 * Shipping labels of orders a third-party carrier took (no Dash shipment):
 * fetches the label data the BE builds from the order and opens the same
 * label modal the Shipments print uses (`useShipmentLabels().open`).
 *
 *   const labels = useShipmentLabels();
 *   const outboundLabels = useOutboundShippingLabels(labels.open);
 *   f.shipmentID ? labels.print([f.shipmentID]) : outboundLabels.print([orderId])
 *
 * `printOrders` prints a mixed selection (outbound list): Dash orders from
 * their shipment, 3PL orders from the order — one modal for all.
 */
export function useOutboundShippingLabels(open: (rows: PackageRow[]) => void) {
  const [preparing, setPreparing] = useState(false);

  const run = useCallback(
    async (load: () => Promise<PackageRow[]>) => {
      setPreparing(true);
      const loadingId = toast.loading('Preparing labels…');
      try {
        open(await load());
      } catch (err) {
        toast.error('Could not load labels', {
          description: errorMessage(err),
        });
      } finally {
        toast.dismiss(loadingId);
        setPreparing(false);
      }
    },
    [open]
  );

  const print = useCallback(
    (outboundOrderIDs: string[]) =>
      run(async () => {
        const { labels } =
          await warehouseApi.getOutboundShippingLabels(outboundOrderIDs);
        return labels.flatMap(outboundLabelToPackageRows);
      }),
    [run]
  );

  const printOrders = useCallback(
    (orders: Pick<OutboundOrder, 'id' | 'fulfillment'>[]) =>
      run(async () => {
        const shipmentIDs = orders
          .map((o) => o.fulfillment?.shipmentID)
          .filter((id): id is string => !!id);
        const thirdPartyIDs = orders
          .filter((o) => hasThirdPartyShippingLabel(o.fulfillment))
          .map((o) => o.id);
        const [shipments, thirdParty] = await Promise.all([
          shipmentIDs.length
            ? shipmentsAPI.bulkItems(shipmentIDs)
            : Promise.resolve([]),
          thirdPartyIDs.length
            ? warehouseApi.getOutboundShippingLabels(thirdPartyIDs)
            : Promise.resolve({ labels: [] }),
        ]);
        return [
          ...shipments.flatMap((s) => shipmentToPackageRows(s)),
          ...thirdParty.labels.flatMap(outboundLabelToPackageRows),
        ];
      }),
    [run]
  );

  return { print, printOrders, preparing };
}
